/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, inject } from "@angular/core";
import { filter, first, map, Subject } from "rxjs";
import {
    ButtonComponent, DatetimePipe, DeleteResourceSectionComponent, FormActionsComponent,
    FormComponent, FormSelectComponent, FormTextareaComponent, IntegrationCardComponent, PropertiesTableComponent,
    PropertiesTableRowComponent, requiredValidator, listPatternValidator,
    MenuItem
} from "typedb-cloud-framework";

import { ApplicationState } from "../../../../service/application-state.service";
import { SnackbarService } from "../../../../service/snackbar.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TeamApi } from "../../../../service/team/team-api.service";
import { RouterLink } from "@angular/router";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import { MFAPolicy } from "../../../../concept/team";
import { toSentenceCase } from "../../../../util";
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe } from "@angular/common";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { emailPattern, emailPatternErrorText } from "typedb-web-common/lib";
import { MatChipInputEvent, MatChipsModule } from "@angular/material/chips";
import { MatDialog } from "@angular/material/dialog";
import { TablePaginatorComponent, TableToolbarAction, TableToolbarComponent } from "../../../../framework/table";
import { UsersTableComponent } from "../../../member/table/users-table.component";
import { TeamController } from "../../../../service/team/team-controller.service";
import { TeamMember, teamMemberProperties } from "../../../../concept/user";
import { Filter } from "../../../../concept/common";
import {
    TeamSetUserAccessLevelDialogComponent
} from "../../set-user-access-level/team-set-user-access-level-dialog.component";
import { TeamAddAdminUserDialogComponent } from "./add-admin/team-add-admin-user-dialog.component";

@Component({
    selector: "tp-security-center-config",
    templateUrl: "./security-center-config.component.html",
    styleUrls: ["./security-center-config.component.scss", "../security-center.scss"],
    imports: [
        RouterLink, AsyncPipe, MatTooltipModule,
        FormComponent, FormActionsComponent, MatFormFieldModule,
        MatSelectModule, ReactiveFormsModule, MatChipsModule, TablePaginatorComponent, TableToolbarComponent, UsersTableComponent
    ]
})
export class SecurityCenterConfigComponent {
    private app = inject(ApplicationState);
    private teamApi = inject(TeamApi);
    private formBuilder = inject(FormBuilder);
    private snackbar = inject(SnackbarService);
    private teamCtl = inject(TeamController);
    private dialog = inject(MatDialog);

    team = this.app.requireCurrentTeam();

    readonly mfaPolicyForm = this.formBuilder.nonNullable.group({
        mfaPolicy: [this.team.mfaPolicy, [requiredValidator]]
    });
    readonly mfaPolicyOptions: { value: MFAPolicy, description: string }[] = [
        { value: "none", description: "Do not require team members to enable MFA" },
        { value: "encouraged", description : "Warn team members that they need to enable MFA without enforcing it" },
        { value: "mandatory", description: "Members will not be able to access team resources until they enable MFA" },
    ];
    readonly mfaPolicyIsSubmitting$ = new Subject<boolean>();

    readonly unsubMembers$ = new Subject<void>();
    readonly admins = this.teamCtl.membersTable(this.unsubMembers$);
    readonly adminOnlyFilter: Filter = {
        property: teamMemberProperties.accessLevel,
        operator: "eq",
        value: { string: "admin" }
    };
    readonly addAdminButton: TableToolbarAction = {
        text: "Add admin",
        enabled: true,
        onClick: () => this.dialog.open(TeamAddAdminUserDialogComponent, { data: { team: this.team } }),
    };

    securityContacts: string[] = [...this.team.securityContacts];
    readonly securityContactsForm = this.formBuilder.nonNullable.group({
        securityContacts: [[...this.team.securityContacts], [listPatternValidator(emailPattern, emailPatternErrorText)]]
    });
    readonly securityContactSeparatorKeysCodes = [ENTER, COMMA] as const;
    readonly securityContactsIsSubmitting$ = new Subject<boolean>();

    constructor() {
        this.admins.displayedProperties = this.admins.displayedProperties.filter((x) => x.id !== "accessLevel");
        this.admins.filters$.pipe(
            filter((filters) => !filters.includes(this.adminOnlyFilter)),
        ).subscribe((filters) => this.admins.filters$.next([...filters, this.adminOnlyFilter]));
    }

    get mfaPolicyChanged() {
        return this.mfaPolicyForm.controls.mfaPolicy.value !== this.team.mfaPolicy;
    }

    submitMFAPolicyForm() {
        if (!this.mfaPolicyChanged) {
            this.mfaPolicyIsSubmitting$.next(false);
            return;
        }

        this.teamApi.updateTeam({
            uuid: this.team.uuid, mfaPolicy: this.mfaPolicyForm.controls.mfaPolicy.value
        }).pipe(first()).subscribe({
            next: () => {
                this.snackbar.success("MFA policy updated");
                this.team.mfaPolicy = this.mfaPolicyForm.controls.mfaPolicy.value;
                this.app.currentTeam$.next(this.team);
                this.mfaPolicyIsSubmitting$.next(false);
            },
            error: () => this.mfaPolicyIsSubmitting$.next(false),
        });
    }

    adminsTableRowPopupMenuItems: (user: TeamMember) => MenuItem[] = (user: TeamMember) =>  [
        {
            label: "Change access level",
            action: () => this.openChangeAccessLevelModal(user),
        },
    ];

    openChangeAccessLevelModal(user: TeamMember) {
        this.dialog.open(TeamSetUserAccessLevelDialogComponent, { data: { team: this.team, user } });
    }

    removeSecurityContact(contact: string) {
        const index = this.securityContacts.indexOf(contact);
        if (index >= 0) this.securityContacts.splice(index, 1);
    }

    addSecurityContact(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value && !this.securityContacts.includes(value)) this.securityContacts.push(value);
        event.chipInput!.clear();
    }

    get addedSecurityContacts() {
        return this.securityContacts.filter(x => !this.team.securityContacts.includes(x));
    }

    get removedSecurityContacts() {
        return this.team.securityContacts.filter(x => !this.securityContacts.includes(x));
    }

    get securityContactsChanged() {
        return this.addedSecurityContacts.length > 0 || this.removedSecurityContacts.length > 0;
    }

    submitSecurityContactsForm() {
        if (!this.securityContactsChanged) {
            this.securityContactsIsSubmitting$.next(false);
            return;
        }
        this.teamApi.updateTeamSecurityContacts(this.team.uuid, this.addedSecurityContacts, this.removedSecurityContacts).subscribe({
            next: () => {
                this.snackbar.success("Security contacts updated");
                this.team.securityContacts = this.securityContacts;
                this.app.currentTeam$.next(this.team);
                this.team = this.app.requireCurrentTeam();
                this.securityContactsIsSubmitting$.next(false);
            },
            error: () => this.securityContactsIsSubmitting$.next(false),
        });
    }

    resetSecurityContacts() {
        this.securityContactsForm.reset();
        this.securityContacts = [...this.team.securityContacts];
    }

    protected readonly toSentenceCase = toSentenceCase;
}
